import type { Session } from "next-auth";
import { signIn, signOut, UpdateSession } from "next-auth/react";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { User } from "~/types";


/**
 * Authentication service with built-in analytics tracking
 */
export const authService = {
  /**
   * Sign in with email (magic link via Mailgun)
   * @param email - User's email address
   * @param callbackUrl - URL to redirect to after sign in
   * @returns Sign in result from NextAuth
   */
  async loginWithEmail(email: string, callbackUrl: string = "/dashboard") {
    // Track sign-in attempt with email
    trackEvent(AnalyticsEvents.USER_LOGIN, { method: "email" });

    return signIn("mailgun", {
      email,
      callbackUrl,
      redirect: false,
    });
  },

  /**
   * Sign in with Google OAuth
   * @param callbackUrl - URL to redirect to after sign in
   */
  async loginWithGoogle(callbackUrl: string = "/dashboard") {
    // Track sign-in attempt with Google
    trackEvent(AnalyticsEvents.USER_LOGIN, { method: "google" });

    return signIn("google", { callbackUrl });
  },

  /**
   * Sign out the user and track the event
   * @param callbackUrl - URL to redirect to after sign out
   */
  async logout(callbackUrl: string = "/"): Promise<void> {
    // Track logout event
    trackEvent(AnalyticsEvents.USER_LOGOUT);

    // Sign out
    await signOut({ callbackUrl });
  },

  async impersonateUser(
    session: Session,
    updateSession: UpdateSession,
    user: User,
  ) {
    if (!session?.user) {
      throw new Error("Nu ești autentificat");
    }

    if (session.user.role !== "admin") {
      throw new Error("Nu ai permisiunea să personifici utilizatori");
    }

    if (user.deletedAt) {
      throw new Error("Nu poți personifica un utilizator șters");
    }

    // Update session with impersonation data
    await updateSession({
      action: "start-impersonate",
      user: {
        id: user.id,
        role: user.role,
        userTrackingId: user.userTrackingId,
        name: user.name,
        email: user.email,
        image: user.image,
      },
    });
  },

  async stopImpersonate(session: Session, updateSession: UpdateSession) {
    if (!session?.user) {
      throw new Error("Nu ești autentificat");
    }

    if (!session.user.isImpersonating) {
      throw new Error("Nu ești în modul de personificare");
    }

    // Update session to stop impersonation
    await updateSession({
      action: "stop-impersonate",
    });
  },
};
